// The design system's ActivityTimeline (counted solid, waiting hatched and not counted, billed lane merged) in
// epoch milliseconds instead of hours of the day, so it also spans midnight (a session's own timeline).
import { useEffect, useRef, useState } from 'react';
import { mergeIntervals, sumIntervals } from '../../../packages/core/src/index.ts';
import { localParts } from '../lib/calendar.ts';
import type { Segment } from '../model/snapshot.ts';
import { useFmt, useStore, useT } from '../store/hooks.tsx';
import { cx, useTip, type Tip } from './ds.tsx';
import { Icon } from './Icon.tsx';

export interface TimelineLane {
  id: string;
  label: string;
  source: 'human' | 'agent';
  segments: Segment[];
}

const HOUR = 3_600_000;

function Segments({ segments, start, span, tip, billedLabel }: { segments: (Segment | { start: number; end: number; kind: 'billed'; label?: string })[]; start: number; span: number; tip: Tip; billedLabel?: string }) {
  const t = useT();
  const fmt = useFmt();
  return (
    <>
      {segments.map((s, i) => {
        const minutes = Math.round((s.end - s.start) / 60_000);
        const range = `${fmt.time(s.start)}–${fmt.time(s.end)}`;
        const content = s.kind === 'billed'
          ? [<b key="b">{s.label ?? billedLabel ?? t('Abgerechnet')}</b>, <span key="s">{t('%@ · %lld min · Überschneidungen 1× gezählt', range, minutes)}</span>]
          : s.kind === 'counted'
            ? [<b key="b">{s.label ?? t('Arbeitet')}</b>, <span key="s">{t('%@ · %lld min gezählt', range, minutes)}</span>]
            : [<b key="b">{s.label ?? t('Wartet auf Eingabe')}</b>, <span key="s">{t('%@ · %lld min nicht gezählt', range, minutes)}</span>];
        return (
          <div key={i} className={`tl-tl__seg tl-tl__seg--${s.kind}`}
            style={{ left: `calc(${((s.start - start) / span) * 100}% + 1px)`, width: `calc(${((s.end - s.start) / span) * 100}% - 2px)` }}
            onMouseEnter={(e) => tip.show(e, content)} onMouseLeave={tip.hide} />
        );
      })}
    </>
  );
}

export interface ActivityTimelineProps {
  start: number;
  end: number;
  now?: number | null;
  tickEveryHours?: number;
  lanes: TimelineLane[];
  /** Adds the billed lane: the union of all counted segments (one project only). */
  merged?: boolean;
  legend?: boolean;
  label?: string;
  className?: string;
}

export function ActivityTimeline({ start, end, now, tickEveryHours = 2, lanes, merged, legend = true, label, className }: ActivityTimelineProps) {
  const t = useT();
  const fmt = useFmt();
  const { timeZone } = useStore();
  const tip = useTip();
  const span = Math.max(1, end - start);
  // Labels at least 48 px apart: narrow screens get fewer ticks.
  const axis = useRef<HTMLDivElement>(null);
  const [axisWidth, setAxisWidth] = useState(0);
  useEffect(() => {
    const el = axis.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => setAxisWidth(entries[0]?.contentRect.width ?? 0));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  let every = tickEveryHours;
  if (axisWidth > 0) {
    for (const candidate of [1, 2, 3, 4, 6, 12, 24].filter((c) => c >= tickEveryHours)) {
      every = candidate;
      if ((axisWidth / (span / HOUR)) * candidate >= 48) break;
    }
  }
  const ticks: number[] = [];
  for (let at = start; at <= end; at += HOUR) {
    if (localParts(at, timeZone).hour % every === 0) ticks.push(at);
  }
  const nowLine = now !== undefined && now !== null && now >= start && now <= end
    ? <div className="tl-tl__now" style={{ left: `${((now - start) / span) * 100}%` }} /> : null;
  const counted = lanes.flatMap((l) => l.segments.filter((s) => s.kind === 'counted'));
  const billed = mergeIntervals(counted);
  const billedLabel = t('Abgerechnet');
  return (
    <div className={cx('tl-tl', className)} role="group" aria-label={label ?? t('Aktivität heute')} data-tl-tiphost="" style={{ position: 'relative' }}>
      <div />
      <div className="tl-tl__axis" ref={axis}>
        {ticks.map((at) => (
          <span key={at} className="tl-tl__tick"
            style={{ left: `${((at - start) / span) * 100}%`, transform: at === start ? 'translateX(0)' : at === end ? 'translateX(-100%)' : undefined }}>
            {fmt.time(at)}
          </span>
        ))}
      </div>
      {lanes.map((l) => [
        <div key={`l${l.id}`} className={`tl-tl__lane tl-tl__lane--${l.source}`}>
          <Icon name={l.source === 'human' ? 'user' : 'terminal'} size={16} strokeWidth={2} />
          <span className="tl-tl__lname">{l.label}</span>
        </div>,
        <div key={`t${l.id}`} className="tl-tl__track">
          <Segments segments={l.segments.map((s) => ({ ...s, start: Math.max(s.start, start), end: Math.min(s.end, end) })).filter((s) => s.end > s.start)}
            start={start} span={span} tip={tip} />
          {nowLine}
        </div>,
      ])}
      {merged ? [
        <div key="ml" className="tl-tl__lane tl-tl__lane--merged">
          <Icon name="euro" size={16} strokeWidth={2} />
          <span className="tl-tl__lname">{billedLabel}<small>{t('%@ statt %@', fmt.duration(sumIntervals(billed)), fmt.duration(sumIntervals(counted)))}</small></span>
        </div>,
        <div key="mt" className="tl-tl__track tl-tl__track--merged">
          <Segments segments={billed.map((b) => ({ start: Math.max(b.start, start), end: Math.min(b.end, end), kind: 'billed' as const }))} start={start} span={span} tip={tip} billedLabel={billedLabel} />
          {nowLine}
        </div>,
      ] : null}
      {legend ? [
        <div key="lg0" />,
        <div key="lg" className="tl-legend tl-tl__legend">
          <span className="tl-legend__item"><span className="tl-legend__sw tl-legend__sw--counted" />{t('Arbeitet · gezählt')}</span>
          <span className="tl-legend__item"><span className="tl-legend__sw tl-legend__sw--waiting" />{t('Wartet · nicht gezählt')}</span>
          {merged ? <span className="tl-legend__item"><span className="tl-legend__sw tl-legend__sw--billed" />{t('Abgerechnet · zusammengeführt')}</span> : null}
        </div>,
      ] : null}
      {tip.node}
    </div>
  );
}

/** A session's strip on its card: counted and waiting between its first and last segment. */
export function SessionStrip({ segments }: { segments: Segment[] }) {
  const tip = useTip();
  if (segments.length === 0) return null;
  const s0 = segments[0]?.start ?? 0;
  const s1 = Math.max(...segments.map((s) => s.end));
  return (
    <div className="tl-agent__strip" data-tl-tiphost="">
      <Segments segments={segments} start={s0} span={Math.max(1, s1 - s0)} tip={tip} />
      {tip.node}
    </div>
  );
}
